import { createApi } from '@reduxjs/toolkit/query/react';
import type {
  PortalCredentialDto,
  PortalCredentialListDto,
  CreatePortalCredentialRequest,
  UpdatePortalCredentialRequest,
} from '@/types';
import { baseQueryWithRetry } from './baseQuery';
import { TAG_PORTAL_CREDENTIAL } from '@/store/tagTypes';

export const portalCredentialsApi = createApi({
  reducerPath: 'portalCredentialsApi',
  baseQuery: baseQueryWithRetry,
  tagTypes: [TAG_PORTAL_CREDENTIAL],
  endpoints: (builder) => ({
    listPortalCredentials: builder.query<PortalCredentialListDto, void>({
      query: () => '/portal-credentials',
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map(({ id }) => ({ type: TAG_PORTAL_CREDENTIAL, id })),
              { type: TAG_PORTAL_CREDENTIAL, id: 'LIST' },
            ]
          : [{ type: TAG_PORTAL_CREDENTIAL, id: 'LIST' }],
    }),

    createPortalCredential: builder.mutation<PortalCredentialDto, CreatePortalCredentialRequest>({
      query: (body) => ({
        url: '/portal-credentials',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: TAG_PORTAL_CREDENTIAL, id: 'LIST' }],
    }),

    updatePortalCredential: builder.mutation<void, { id: string } & UpdatePortalCredentialRequest>({
      query: ({ id, ...body }) => ({
        url: `/portal-credentials/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: TAG_PORTAL_CREDENTIAL, id },
        { type: TAG_PORTAL_CREDENTIAL, id: 'LIST' },
      ],
    }),

    validatePortalCredential: builder.mutation<void, string>({
      query: (id) => ({ url: `/portal-credentials/${id}/validate`, method: 'POST' }),
      invalidatesTags: [TAG_PORTAL_CREDENTIAL],
    }),

    deletePortalCredential: builder.mutation<void, string>({
      query: (id) => ({
        url: `/portal-credentials/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: TAG_PORTAL_CREDENTIAL, id },
        { type: TAG_PORTAL_CREDENTIAL, id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useListPortalCredentialsQuery,
  useCreatePortalCredentialMutation,
  useUpdatePortalCredentialMutation,
  useDeletePortalCredentialMutation,
  useValidatePortalCredentialMutation,
} = portalCredentialsApi;
